import React, {ReactElement} from "react";

import {css, generateKeyId} from "/src/utils/utils";
import {MaybeType} from "/src/types";

import CTAButton, {CTA} from "../../buttons/cta-button/CTAButton";
import Image, {ImageObject} from "../../image/Image";
import ModuleBody from "../../layouts/module-body/ModuleBody";
import Video, {VideoType} from "../../videos/video/Video";
import Icon from "../../icon/Icon";

import FlexportLogoIcon from "/src/svg/flexport-light.svg";

import * as styles from "./home-page-hero.module.scss";
import translate from "/src/utils/translate";
import ReactPlayer from "react-player";

interface HomePageHeroProps {
  titleWithLineBreaks: MaybeType<string>;
  ctas: MaybeType<Array<CTA>>;
  copy: MaybeType<string>;
  skusAnimation: string;
  exceptionsAnimation: string;
  mapAnimation: string;
  messageIcon: ImageObject;
  searchIcon: ImageObject;
  profilePhoto: ImageObject;
  informationBellIcon: ImageObject;
  homepageHeroBackground: ImageObject;
}

const HomePageHero = (props: HomePageHeroProps): ReactElement => {
  const {
    titleWithLineBreaks,
    ctas,
    copy,
    skusAnimation,
    exceptionsAnimation,
    mapAnimation,
    messageIcon,
    searchIcon,
    profilePhoto,
    informationBellIcon,
    homepageHeroBackground,
  } = props;

  const [currentAnimation, setCurrentAnimation] = React.useState(0);
  const [redDot, setRedDot] = React.useState<boolean>(false);
  const mapRef = React.useRef<ReactPlayer | null>(null);
  const exceptionsRef = React.useRef<ReactPlayer | null>(null);
  const skusRef = React.useRef<ReactPlayer | null>(null);

  React.useEffect(() => {
    let intDuration;
    if (currentAnimation === 0) {
      if (mapRef.current !== null) {
        mapRef.current.seekTo(0);
      }
      if (exceptionsRef.current !== null) {
        exceptionsRef.current.seekTo(0);
      }
      if (skusRef.current !== null) {
        skusRef.current.seekTo(0);
      }
      intDuration = 2200;
    }
    if (currentAnimation === 1) intDuration = 1300;
    if (currentAnimation === 2) intDuration = 2000;
    if (currentAnimation === 3) intDuration = 1900;
    const interval = setInterval(() => {
      setCurrentAnimation(() => {
        if (currentAnimation === 3) return 0;
        return currentAnimation + 1;
      });
    }, intDuration);
    return () => clearInterval(interval);
  }, [currentAnimation, setCurrentAnimation]);

  React.useEffect(() => {
    const redDotInterval = setInterval(() => {
      setRedDot(() => {
        if (
          redDot === false ||
          currentAnimation === 2 ||
          currentAnimation === 3
        ) {
          return true;
        }
        return false;
      });
    }, 300);

    return () => clearInterval(redDotInterval);
  }, [redDot, setRedDot, currentAnimation]);

  return (
    <div className={styles.homePageHero}>
      <ModuleBody classes={styles.homePageHero__body}>
        <div className={styles.homePageHero__bgImageWrapper}>
          <Image
            objectFit="cover"
            image={homepageHeroBackground}
            classes={styles.homePageHero__bgImage}
          />
        </div>

        <div className={styles.homePageHero__left}>
          <div className={styles.homePageHero__left__content}>
            <h1 className={css("h1", styles.homePageHero__left__headline)}>
              <span
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html:
                    titleWithLineBreaks != null
                      ? titleWithLineBreaks.replace(/(\n|\r)/g, "<br/>")
                      : "",
                }}
              />
            </h1>

            <p className={css("p", styles.homePageHero__left__copy)}>{copy}</p>

            {/* we will also assume that the first CTA would be primary, subsequent cta should be secondary */}
            {ctas && (
              <ul className={styles.homePageHero__left__ctas}>
                {ctas.map((cta, i) => {
                  return (
                    <li
                      key={generateKeyId(`${cta.label.toLowerCase()}-${i}`)}
                      className={styles.homePageHero__left__cta}
                    >
                      <CTAButton
                        darkMode
                        cta={cta}
                        kind={i === 0 ? "primary" : "secondary"}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className={styles.homePageHero__right}>
          <div className={css(styles.homePageHero__right__animationWrapper)}>
            <div className={styles.homePageHero__right__nav}>
              <div className={styles.homePageHero__right__leftNav}>
                <div
                  className={styles.homePageHero__right__leftNav__logoWrapper}
                >
                  <Icon
                    svg={FlexportLogoIcon}
                    role="img"
                    alt="Flexport"
                    classes={styles.homePageHero__right__leftNav__logo}
                  />
                </div>

                <div className={styles.homePageHero__right__iconWrapper}>
                  <Image
                    image={searchIcon}
                    classes={styles.homePageHero__right__icon}
                  />
                </div>
              </div>

              <div className={styles.homePageHero__right__rightNav}>
                {redDot && currentAnimation !== 0 && (
                  <div
                    className={styles.homePageHero__right__rightNav__redDot}
                  ></div>
                )}
                <div className={styles.homePageHero__right__iconWrapper}>
                  <Image
                    image={messageIcon}
                    classes={styles.homePageHero__right__icon}
                  />
                </div>
                <div className={styles.homePageHero__right__iconWrapper}>
                  <Image
                    image={informationBellIcon}
                    classes={styles.homePageHero__right__icon}
                  />
                </div>
                <div className={styles.homePageHero__right__iconWrapper}>
                  <Image
                    image={profilePhoto}
                    classes={styles.homePageHero__right__icon}
                  />
                </div>
              </div>
            </div>

            <div className={styles.homePageHero__divider}></div>

            <h4 className={css("h4", styles.homePageHero__right__headline)}>
              {translate("Hello Sofia!")}
            </h4>

            <div
              className={css(
                styles.homePageHero__right__mapAnimation,
                currentAnimation === 0
                  ? styles.homePageHero__right__mapAnimationAnimate
                  : ""
              )}
            >
              <Video
                videoTitle="Flexport Map Animation"
                videoRef={mapRef}
                showControl={false}
                videoSource={mapAnimation}
                type={VideoType.autoplay}
              />
            </div>

            <div className={styles.homePageHero__right__bottomAnimation}>
              <div
                className={css(
                  styles.homePageHero__right__exceptionAnimation,
                  currentAnimation === 1
                    ? styles.homePageHero__right__exceptionAnimationAnimate
                    : ""
                )}
              >
                <Video
                  videoTitle="Flexport Map Animation"
                  videoRef={exceptionsRef}
                  videoSource={exceptionsAnimation}
                  showControl={false}
                  type={VideoType.autoplay}
                />
              </div>

              <div
                className={css(
                  styles.homePageHero__right__skusAnimation,
                  currentAnimation === 2
                    ? styles.homePageHero__right__skusAnimationAnimate
                    : ""
                )}
              >
                <Video
                  videoTitle="Flexport Map Animation"
                  videoRef={skusRef}
                  showControl={false}
                  videoSource={skusAnimation}
                  type={VideoType.autoplay}
                />
              </div>
            </div>
          </div>
        </div>
      </ModuleBody>
    </div>
  );
};

export default HomePageHero;
